<script setup lang="ts">
/**
 * The one question asked when an account arrives on a device that already holds work.
 *
 * A workspace belongs to an account now, and this device's original one belongs to nobody: it
 * predates accounts entirely. So the first account to sign in here is asked whether that work is
 * theirs. Nothing is copied and nothing is deleted either way — saying yes writes an owner into a
 * ledger, and saying no leaves the work exactly where it is for whoever does claim it.
 *
 * It is asked ONCE per account, and only when there is something to ask about.
 */
import Icon from './Icon.vue';

defineProps<{ email: string; records: number }>();

const emit = defineEmits<{ (e: 'keep'): void; (e: 'separate'): void }>();
</script>

<template>
  <div class="scrim">
    <div
      class="ask e-enter" role="dialog" aria-modal="true" aria-labelledby="adopt-title"
      data-testid="adopt-work"
    >
      <Icon name="paper" :size="20" class="mark" />
      <h2 id="adopt-title" class="title">This device already has work on it</h2>
      <p class="note">
        <span class="num">{{ records }}</span>
        {{ records === 1 ? 'record was' : 'records were' }} saved here before anyone signed in.
        Keeping them puts them in <span class="lift">{{ email }}</span>, on this device and on any
        other you sign in on.
      </p>
      <p class="note">
        Starting separately leaves them here untouched — they stay on this device, and this
        account begins empty.
      </p>
      <div class="actions">
        <button
          class="e-btn e-btn--outline" type="button" data-testid="adopt-separate"
          @click="emit('separate')"
        >Start separately</button>
        <button
          class="e-btn e-btn--primary" type="button" data-testid="adopt-keep"
          @click="emit('keep')"
        >Keep this work</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.scrim {
  position: fixed; inset: 0; z-index: 60;
  display: grid; place-items: center; padding: var(--sp-4);
  background: var(--scrim);
}

.ask {
  width: min(460px, 100%); padding: var(--sp-5);
  display: grid; gap: var(--sp-3);
  background: var(--chrome-raised);
  border: 1px solid var(--keyline);
  border-radius: var(--r-panel);
  box-shadow: var(--el-3);
}

.mark { color: var(--ink-faint); }
.title { margin: 0; font: 600 var(--t-h2) / 1.2 var(--font-sans); color: var(--ink); }
.note { margin: 0; font: 400 var(--t-sm) / 1.55 var(--font-sans); color: var(--ink-muted); }
.note .num { color: var(--ink); font-family: var(--font-mono); }
.lift { color: var(--ink); }

.actions { display: flex; justify-content: flex-end; gap: var(--sp-2); margin-top: var(--sp-2); }
.actions .e-btn { min-height: 44px; }
</style>
